import _ from 'lodash';
import { sqWorkbenchStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getODataDefaultProtocol, isODataLegacyEnabled } from '@/services/systemConfiguration.utilities';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { ODATA_V2_PROTOCOL } from '@/tools/exportOData/odata.constants';
import {
  ODATA_EXPORTS_PER_PAGE,
  ODATA_EXPORT_NAME_SEPARATOR_REGEX,
} from '@/tools/viewODataExports/oDataExports.constants';
import { getItemType } from '@/trendData/trend.actions';
import { errorToast } from '@/utilities/toast.utilities';
import { sqExportApi } from '@seeqdev/api/endpoint/ExportApi';
import { OrderByEnum, sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';

/**
 * Load view odata exports and set the items
 */
export async function loadViewODataExports(hasNextPage: boolean, itemsLength: number) {
  flux.dispatch('SET_LOADING_ODATA_EXPORTS', true);
  try {
    const {
      data: { items, next },
    } = await sqItemsApi.searchItems({
      filters: [SeeqNames.API.Flags.ExcludeGloballyScoped],
      scope: [sqWorkbenchStore.stateParams.workbookId],
      types: [SeeqNames.Types.Export],
      includeProperties: [SeeqNames.Properties.ExportData],
      orderBy: [OrderByEnum.Name] as any,
      offset: hasNextPage ? itemsLength : 0,
      limit: ODATA_EXPORTS_PER_PAGE,
    });

    // Add in `exportName` from the `Export Data` item property which is the name not altered to be Odata character safe
    items?.forEach((item) => {
      const exportData = _.get(item.includedProperties, SeeqNames.Properties.ExportData);
      if (exportData !== undefined) {
        const { exportName, worksheetLink } = JSON.parse(exportData.value);
        _.set(item, 'exportName', exportName);
        _.set(item, 'worksheetLink', worksheetLink);
      }
    });

    flux.dispatch('SET_VIEW_ODATA_EXPORTS', { items, hasNextPage: !!next });
    flux.dispatch('SET_LOADING_ODATA_EXPORTS', false);
  } catch (error) {
    errorToast({ httpResponseOrError: error, displayForbidden: true });
  }
}

export function loadNextPageActions(shouldGetNextPageItems: boolean, itemsLength: number) {
  flux.dispatch('SEARCH_LOAD_NEXT_PAGE', {}, PUSH_IGNORE);

  loadViewODataExports(shouldGetNextPageItems, itemsLength);
}

/**
 * Set odata export for edit
 *
 * @param {string} id - OData Export id
 *
 * @return {Promise<string | undefined>} - returns OData Export href link
 */
export function setODataExport(id: string): Promise<{ dataSetLink: string; serviceURL: string }> {
  return sqExportApi.getExport({ id }).then(async (info) => {
    const odata = info.data;

    const getODataExportProperties = (id: string) =>
      sqItemsApi.getItemAndAllProperties({ id }).then(({ data }) => {
        const valueUnitOfMeasure = _.chain(data.properties)
          .find(['name', SeeqNames.Properties.ValueUom])
          .get('value')
          .value();

        return {
          id: data.id,
          name: data.name,
          itemType: getItemType(data),
          valueUnitOfMeasure,
        };
      });

    _.chain(odata.items)
      .map((item) => getODataExportProperties(item.id))
      .thru((promises) => Promise.all(promises))
      .value()
      .then((additionalItems) => {
        flux.dispatch('SET_ODATA_EXPORT', { odata, additionalItems });
      });

    return {
      dataSetLink:
        getODataDefaultProtocol() === ODATA_V2_PROTOCOL || !isODataLegacyEnabled()
          ? odata.href || ''
          : odata.hrefLegacy || '',
      serviceURL: odata.href || '',
    };
  });
}

/**
 * Delete odata export by id
 *
 * @param {string} id - odata export id
 */
export async function deleteODataExport(id: string) {
  await sqExportApi.archiveExport({ id }).then(() => {
    flux.dispatch('DELETE_ODATA_EXPORT', id);
  });
}

/**
 * Get the OData Link by export id
 *
 * @param {string} id - OData Export id
 *
 * @returns {Promise<string | undefined>} href - export link
 */
export function getODataLink(id: string): Promise<{ dataSetLink: string; serviceURL: string }> {
  return sqExportApi.getExport({ id }).then(({ data: { href = '', hrefLegacy = '' } }) => {
    return {
      dataSetLink: getODataDefaultProtocol() === ODATA_V2_PROTOCOL || !isODataLegacyEnabled() ? href : hrefLegacy,
      serviceURL: href,
    };
  });
}

/**
 * Restore OData Export
 *
 * @param {string} id - OData Export id used for restore
 * @param {string} worksheetLink - OData Export worksheet link used view/edit export after restore
 */
export async function restoreODataExport(id: string, worksheetLink: string): Promise<void> {
  await sqItemsApi.setProperty({ value: false }, { id, propertyName: 'archived' });
  return sqExportApi.getExport({ id }).then(({ data }) => {
    setODataNewElement({ id: data.id, exportName: data.exportName, worksheetLink });
  });
}

export function setODataNewElement(oDataInfo: { id: string; exportName?: string; worksheetLink?: string }) {
  flux.dispatch('SET_VIEW_ODATA_EXPORT_ELEMENT', oDataInfo);
}

/**
 * Redirect to OData Export used for edit
 */
export function goToExportData() {
  flux.dispatch('INVESTIGATE_SET_ACTIVE_TOOL', { tool: TREND_TOOLS.EXPORT_ODATA });
}

/**
 * Partition the export name to extract the user provided name and the unique export
 * identifier segment
 *
 * @param {string} exportName - The OData export name not altered to be Odata character safe
 *
 * @returns {object} - The partitioned export name
 */
export function getODataExportNameParts(exportName: string): { name: string; guid: string; capsule: string } {
  const matches = exportName.match(ODATA_EXPORT_NAME_SEPARATOR_REGEX);
  if (matches && matches.length >= 4) {
    const name = matches[1];
    // Disregard matches[2] which is the worksheet name
    const guid = matches[3];
    const capsule = matches[4] || '';
    return { name, guid, capsule };
  }
  return { name: exportName, guid: '', capsule: '' };
}
